import Reflux from "reflux";

import Immutable, { List, fromJS } from "immutable";
import IDUtils from "../utils/IDUtils";
import AppConstants from "../constants/AppConstants";
import { splitCanvasSet } from "../utils/CanvasSetUtils";
import GroupStore from "./GroupStore";
import GroupActions from "@/actions/GroupActions";
import BlockUtils from "@/utils/BlockUtils";
import { getContainerData } from "@/utils/GroupUtils";
import { isIOS } from "@/constants/BrowserConstants";

/**
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS} Layouts
 * @typedef {Layouts[number]} Layout
 * @typedef {import("@/entities/SiteDesign").default} SiteDesign
 * @typedef {import("@/types/data/CanvasSet.mjs").CanvasSet} CanvasSet
 * @typedef {import("@/types/data/DesignConfig.mjs").DesignConfig} DesignConfig
 * @typedef {import("@/entities/SiteDesign").SiteItem} CurrentPageItem
 */

/**
 * @typedef {Object} ActiveContainer
 * @prop {*} blocks - array of blocks. Not the entity
 * @prop {Record<string, CanvasSet>} canvasSets - type this
 * @prop {DesignConfig} config - design config from site JSON - type this
 * @prop {CanvasSet=} currentCanvasSet - Canvas set data if active container
 * @prop {CurrentPageItem=} currentPageItem
 * @prop {*} data
 * @prop {string} id
 * @prop {boolean} isBlock
 * @prop {boolean} isCanvasSet
 * @prop {boolean} isPage
 * @prop {boolean} isWP
 * @prop {Layout} layout
 * @prop {string} name
 * @prop {*} selectedBlock
 * @prop {string} slug
 * @prop {*} theme
 * @prop {"page"} type
 * @prop {boolean} wpEnabled
 */

/**
 * @typedef {import("@/types/TypeUtils.mjs").ImmutableMapFrom<ActiveContainer>} ActiveContainerState
 */

const {
  BLOCK_PROP_PARENT_BLOCK,
  BLOCK_PROP_PARENT_BLOCK_ID,
  CANVAS_SET_CHILDREN,
  CANVAS_SET_DATA,
  SITE_ITEM_BLOCK,
  SITE_ITEM_PAGE,
  SITE_ITEM_SITE,
  SITE_ITEM_CANVAS_SET,
} = AppConstants;

const model = {
  id: null,
  type: null,
  slug: null,
  wpEnabled: false,
  isPage: false,
  isBlock: false,
  isCanvasSet: false,
  blocks: [],
  selectedBlock: {},
  theme: {},
  layout: isIOS ? AppConstants.LAYOUT_DESKTOP : AppConstants.LAYOUT_MOBILE,
};

const PAGE_CONTENT_DUMMY_BLOCK = {
  mobile: {
    w: 320,
    h: 200,
  },
  states: [],
  name: "Dummy Canvas",
  slug: "new-canvas",
  inert: "true",
  visible: "a",
  elements: [
    {
      type: "text",
      visible: "a",
      content: {
        text: "Page Content",
        sample: "Page Content",
      },
      mobile: {
        w: 102.60000000000001,
        h: 19,
        x: 109,
        y: 91,
        a: 0,
        style: "paragraph",
        c: "#ffffff:70",
      },
      desktop: {
        w: 118.8,
        h: 22,
        x: 541,
        y: 189,
        a: 0,
        style: "paragraph",
        tA: "center",
        c: "#ffffff:70",
      },
      style: "paragraph",
    },
  ],
  type: "block",
  id: "dummy_page_content_block",
  sync: ["bgMediaType", "bgFillType", "bgColor"],
  desktop: {
    w: 1200,
    h: 400,
    bgMediaType: "none",
    bgFillType: "color",
    bgColor: "#000000:20",
  },
};

/**
 * This store contains the data for the block container.
 * A block container can either be a single site block or page
 *
 * It also contains data about the selected layout and block
 */
const ActiveBlockContainerStore = Reflux.createStore({
  storeName: "ActiveBlockContainerStore",
  async init() {
    // Dynamic import breaks circular dependency:
    // SelectionStore → ActiveBlockContainerStore → SiteDesignStore → SelectionStore
    // Static import would cause undefined exports at module initialization time.
    this.siteDesignStore = (await import("./SiteDesignStore")).default;
    this.listenTo(this.siteDesignStore, this.onDesignChange);

    // internal use only
    this.data = Immutable.Map();
    this.selected = Immutable.Map();

    this.config = null;
    this.id = null;
    this.type = null;
    this.slug = null;
    this.name = null;

    this.wpEnabled = false;
    this.blocks = Immutable.List();
    this.theme = Immutable.Map();

    this.selectedBlock = Immutable.Map();

    this.selectedLayout = isIOS ? AppConstants.LAYOUT_DESKTOP : AppConstants.LAYOUT_MOBILE;

    this.currentPageItem = null;
    this.currentCanvasSet = null;

    /** @type {ActiveContainerState} */
    this.container = Immutable.fromJS(model);
  },

  onDesignChange() {
    const siteDesign =
      this.siteDesignStore.getDesign(); /** SiteDesign Entity */
    const blockContainer =
      this.siteDesignStore.getBlockContainer(); /** BlockContainer Entity */
    const selected = this.siteDesignStore.getSelected();
    const currentPage = this.siteDesignStore.getCurrentPage();
    const currentCanvasSet = this.siteDesignStore.selected.get("canvasSetId");

    this.theme = siteDesign.theme;
    this.selected = selected;
    this.data = blockContainer.data;
    this.config = siteDesign.config;

    if (this.selected.get("blockId") === null) {
      this.selectedBlock = this.selectedBlock.clear();
    }

    if (currentPage) {
      this.currentPageItem = siteDesign.getPageItem(currentPage.id);
      this.currentCanvasSet = siteDesign.getCanvasSet({ id: currentCanvasSet });
      this.canvasSets = siteDesign.getCanvasSets();
    }

    this.selectedLayout = this.selected.get("layout");

    const containerType = blockContainer.type;
    const blockId = this.selected.get("blockId");
    const selectedCanvasSetId = this.selected.get("canvasSetId");

    if (containerType === SITE_ITEM_SITE) {
      if (
        this.currentCanvasSet &&
        (!blockId || (selectedCanvasSetId && blockId))
      ) {
        this.type = SITE_ITEM_CANVAS_SET;
        this.id = this.selected.get("canvasSetId"); // The id for the container will be the selected canvas set
        this.slug = this.data.getIn([CANVAS_SET_DATA, this.id, "slug"]);
        this.name = this.data.getIn([CANVAS_SET_DATA, this.id, "name"]);
        this.wpEnabled = true;
      } else {
        this.type = SITE_ITEM_BLOCK;
        this.id = this.selected.get("blockId"); // The id for the container will be the selected block
        this.slug = "index";
        this.name = "Site Canvas";
        this.wpEnabled = true;
      }
    }

    if (containerType === SITE_ITEM_PAGE) {
      this.type = containerType;
      this.id = this.data.get("id");
      this.slug = this.data.get("slug");
      this.name = this.data.get("name");
      this.wpEnabled = this.data.get("wp", null) != null;
      this.currentCanvasSet = siteDesign.getPageCanvasSetList({
        id: currentCanvasSet,
      });
    }

    this._loadBlocks(blockContainer, siteDesign);

    this._setupContainer();

    this.trigger();
  },

  _setupContainer() {
    const {
      id,
      config,
      type,
      name,
      slug,
      blocks,
      selectedLayout: layout,
      theme,
      selectedBlock,
      wpEnabled: isWP,
      currentPageItem,
      currentCanvasSet,
      canvasSets,
      data,
    } = this;

    this.container = this.container.merge({
      id,
      config,
      type,
      name,
      slug,
      blocks,
      layout,
      theme,
      selectedBlock,
      isPage: type === SITE_ITEM_PAGE,
      isBlock: type === SITE_ITEM_BLOCK,
      isCanvasSet: type === SITE_ITEM_CANVAS_SET,
      isWP,
      currentPageItem,
      currentCanvasSet,
      canvasSets,
      data, // the raw structure for the container,
    });
  },

  /**
   * Get current container state
   * @returns {ActiveContainerState}
   */
  getContainer() {
    return this.container;
  },

  /**
   * Get current layout
   * @returns {Layout}
   */
  getSelectedLayout() {
    return this.selectedLayout;
  },

  /**
   * Adds site blocks to beginning and end of blocks array, goes in to out through the list of canvas sets
   * @param {string[]} canvasSetIds - canvasSetIds
   * @param {Map[]} blocks - an array of Immutable Maps
   * @param {SiteDesign} siteDesign
   */
  _addCanvasSetsToData(canvasSetIds, blocks, siteDesign) {
    canvasSetIds &&
      canvasSetIds.forEach((canvasSetId) => {
        const getBlockCB = (position) => (acc, id) => {
          const block = siteDesign.getBlock(id);
          return block
            ? acc.push(
                block
                  .set("pBlockId", id)
                  .set("pBlock", block)
                  .set("canvasSet", canvasSetId)
                  .set("canvasSetPosition", position),
              )
            : acc;
        };

        const canvasSetBlockIdsList = siteDesign.getCanvasSet({
          id: canvasSetId,
        });

        if (!canvasSetBlockIdsList || !canvasSetBlockIdsList.size) return;

        const [top, bottom] = splitCanvasSet(
          canvasSetBlockIdsList.get(CANVAS_SET_CHILDREN),
        ).asImmutable();
        const topBlocks = top.reduce(getBlockCB("top"), List());
        const bottomBlocks = bottom.reduce(getBlockCB("bottom"), List());

        blocks.unshift(...topBlocks);
        blocks.push(...bottomBlocks);
      });
  },

  _loadBlocks(blockContainer, siteDesign) {
    const { id, type } = this;
    let blocks = [];

    switch (type) {
      case SITE_ITEM_BLOCK: // site block as container
        const block = blockContainer.getBlock(id);
        if (block) {
          blocks.push(this._setBlockSelection(block, blocks.length));
          this.name = block.get("name"); // set name of container
        }
        break;
      case SITE_ITEM_PAGE:
        blockContainer.getBlockOrder().map((blockId) => {
          let block = blockContainer.getBlock(blockId);
          if (block) {
            if (block.has(BLOCK_PROP_PARENT_BLOCK_ID)) {
              const siteBlock = siteDesign.getBlock(
                block.get(BLOCK_PROP_PARENT_BLOCK_ID),
              );

              if (siteBlock) {
                block = block.set(BLOCK_PROP_PARENT_BLOCK, siteBlock);
              }
            }

            blocks.push(block);
          }
        });

        const page = siteDesign.getPageItem(id);
        if (!page) {
          break;
        }
        const pageCanvasSetIds = page.get(SITE_ITEM_CANVAS_SET);
        this._addCanvasSetsToData(pageCanvasSetIds, blocks, siteDesign);
        blocks = blocks.map((block) => this._setBlockSelection(block));
        break;
      case SITE_ITEM_CANVAS_SET:
        blocks.push(fromJS(PAGE_CONTENT_DUMMY_BLOCK));
        this._addCanvasSetsToData(List([id]), blocks, siteDesign);
        blocks = blocks.map((block) => this._setBlockSelection(block));
        break;
      default:
    }
    this.blocks = Immutable.List(blocks);
  },

  _setBlockSelection(block) {
    const selectedId = this.selected.get("blockId");
    const selectedLayout = this.selected.get("layout");
    const selectedState = this.selected.get("blockState");

    if (block.get("id") === selectedId) {
      block = block.set("selected", this.selected);
      /** @type {import("@/types/TypeUtils.mjs").ImmutableMapFrom<Record<string, import("@/entities/Block").ElementInfo>>} */
      const elements = this.selected.get("elements");

      elements.map((elemInfo, elemId) => {
        const idPath = IDUtils.getArray(elemId, 1); // popoff first value since it is blockId
        block = block.setIn(
          idPath.concat("selected"),
          Immutable.fromJS({
            layout: selectedLayout,
            id: elemId,
            blockId: selectedId,
            edit: elemInfo?.edit ?? false,
          }),
        );
      });

      if (selectedState !== null) {
        block = block.setIn(
          IDUtils.getArray(selectedState, 1).concat("selected"),
          Immutable.fromJS({
            layout: selectedLayout,
            id: selectedState,
          }),
        );
      }

      this.selectedBlock = block;

      const contained = elements.some((elem) => elem?.contained);
      const baseElement = elements.find((elem) => elem.contained);
      const container = baseElement && getContainerData(block, baseElement.id);
      const containerHidden =
        container && !BlockUtils.isVisibleInLayout(container, selectedLayout);

      if (container && !GroupStore.isFocused()) {
        !containerHidden &&
          GroupActions.focusGroup({
            groupSelectionId: baseElement?.containedBy,
            selectedBlock: this.selectedBlock,
          });
      } else if (
        GroupStore.isFocused() &&
        ((contained && containerHidden) || !contained)
      ) {
        GroupActions.blurGroup();
      }
    }

    return block;
  },
});

export default ActiveBlockContainerStore;
